import React, { useEffect, useState } from "react";
import { api, apiError, peso } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Megaphone, ShieldAlert } from "lucide-react";
import FeaturedProfessionalsAdmin from "@/components/admin/FeaturedProfessionalsAdmin";
import SponsoredAdsAdmin from "@/components/admin/SponsoredAdsAdmin";

const TAB_KEYS = ["overview", "sponsored", "featured", "campaigns", "review", "inventory", "credits", "payments", "pricing"];
const SPOTLIGHT_PLACEMENTS = ["DASHBOARD_SPOTLIGHT", "MARKETPLACE_HERO_SPOTLIGHT"];

const Pill = ({ className = "", children }) => (
  <span className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${className}`}>{children}</span>
);
const FlagPill = ({ on }) => (
  <Pill className={on ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}>{on ? "ON" : "OFF"}</Pill>
);
const SourcePill = ({ s }) => {
  const m = { LEGACY_HOUSE: "bg-amber-100 text-amber-800", HOUSE: "bg-indigo-100 text-indigo-700", SELF_SERVICE: "bg-emerald-100 text-emerald-700" };
  return <Pill className={m[s] || "bg-slate-100 text-slate-600"}>{s}</Pill>;
};
const Empty = ({ children }) => (
  <div className="text-sm text-slate-500 bg-slate-50 border border-dashed border-slate-200 rounded-xl px-4 py-8 text-center" data-testid="mc-empty">{children}</div>
);
const Card = ({ children, className = "" }) => (
  <div className={`bg-card border border-border rounded-xl ${className}`}>{children}</div>
);

function DormantBanner({ flags }) {
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3" data-testid="mc-dormant-banner">
      <ShieldAlert className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
      <div className="text-sm text-amber-900">
        <div className="font-semibold">Monetization Status: NOT ACTIVATED</div>
        <div className="text-amber-800/90 mt-0.5">Listing Credit Enforcement: OFF · Customer Payments: NOT ACTIVATED. This console is read-only; it cannot change any feature flag.</div>
      </div>
    </div>
  );
}

// ---------- Overview ----------
function OverviewTab({ cfg, legacy, managed }) {
  const flags = cfg?.flags || {};
  const byState = {};
  (managed || []).forEach((c) => { byState[c.lifecycle_state] = (byState[c.lifecycle_state] || 0) + 1; });
  const FLAG_LABELS = {
    ADV_SELF_SERVICE_ENABLED: "Self-Service Advertising", LISTING_CREDITS_ENFORCED: "Listing Credit Enforcement",
    ADV_FEATURED_PROPERTY_ENABLED: "Featured Property", ADV_PROMOTED_EVENT_ENABLED: "Promoted Event",
    ADV_SPOTLIGHT_SELF_SERVE_ENABLED: "Managed Spotlight", ADV_MARKETPLACE_HERO_ENABLED: "Marketplace Hero",
    PAYMENT_ENFORCEMENT_ENABLED: "Payment Enforcement",
  };
  return (
    <div className="space-y-5" data-testid="mc-overview">
      <DormantBanner flags={flags} />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <Metric label="Legacy campaigns" value={legacy?.length ?? 0} testid="mc-ov-legacy" />
        <Metric label="Managed campaigns" value={managed?.length ?? 0} testid="mc-ov-managed" />
        <Metric label="Pricing packages" value={(cfg?.packages || []).length} testid="mc-ov-packages" />
        <Metric label="Placements" value={(cfg?.placement_registry || []).length} testid="mc-ov-placements" />
      </div>
      <Card className="p-4">
        <div className="text-sm font-semibold text-slate-800 mb-3">Feature flags (read-only)</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6">
          {Object.keys(FLAG_LABELS).map((k) => (
            <div key={k} className="flex items-center justify-between border-b border-slate-100 py-1.5" data-testid={`mc-flag-${k}`}>
              <span className="text-sm text-slate-600">{FLAG_LABELS[k]}</span><FlagPill on={!!flags[k]} />
            </div>
          ))}
        </div>
      </Card>
      {Object.keys(byState).length > 0 && (
        <Card className="p-4">
          <div className="text-sm font-semibold text-slate-800 mb-2">Managed campaigns by lifecycle</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(byState).map(([s, n]) => <Pill key={s} className="bg-slate-100 text-slate-700">{s}: {n}</Pill>)}
          </div>
        </Card>
      )}
    </div>
  );
}
function Metric({ label, value, testid }) {
  return (
    <div className="bg-card border border-border rounded-xl p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="font-display text-2xl font-bold text-slate-900 mt-1">{value}</div>
    </div>
  );
}

// ---------- Campaigns ----------
function CampaignsTab({ legacy }) {
  const [src, setSrc] = useState("ALL");
  const rows = (legacy || [])
    .map((c) => ({
      ...c,
      _source: c.lifecycle_state ? (c.campaign_kind || c.campaign_source || "HOUSE") : "LEGACY_HOUSE",
      _state: c.lifecycle_state || "—",
    }))
    .filter((r) => src === "ALL" || r._source === src);
  return (
    <div className="space-y-3" data-testid="mc-campaigns">
      <div className="flex gap-2 flex-wrap">
        {["ALL", "LEGACY_HOUSE", "HOUSE", "SELF_SERVICE"].map((s) => (
          <button key={s} data-testid={`mc-camp-filter-${s}`} onClick={() => setSrc(s)}
            className={`text-xs px-3 py-1.5 rounded-full border ${src === s ? "bg-[#0F2C59] text-white border-[#0F2C59]" : "bg-white text-slate-600 border-slate-200"}`}>{s}</button>
        ))}
      </div>
      {rows.length === 0 ? <Empty>No campaigns yet.</Empty> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm"><thead><tr className="text-left text-[11px] uppercase text-slate-400 border-b border-slate-200">
            <th className="px-3 py-2">Campaign</th><th className="px-3 py-2">Product</th><th className="px-3 py-2">Source</th>
            <th className="px-3 py-2">Placement</th><th className="px-3 py-2">Window</th><th className="px-3 py-2">Lifecycle</th></tr></thead>
            <tbody>{rows.map((r, i) => (
              <tr key={r.id || i} className="border-b border-slate-100" data-testid={`mc-camp-row-${r.id}`}>
                <td className="px-3 py-2.5 max-w-[220px] truncate">{r.title || r.campaign_name || r.campaign_type || r.id}</td>
                <td className="px-3 py-2.5">{r.product || "—"}</td>
                <td className="px-3 py-2.5"><SourcePill s={r._source} /></td>
                <td className="px-3 py-2.5"><span className="text-[11px] font-semibold text-[#0F2C59]">{r.placement}</span></td>
                <td className="px-3 py-2.5 whitespace-nowrap text-xs text-slate-500">{r.start_date ? `${r.start_date} · ${r.duration_days}d` : "—"}</td>
                <td className="px-3 py-2.5">{r._state === "—" ? <span className="text-slate-400">legacy</span> : <Pill className="bg-slate-100 text-slate-700">{r._state}</Pill>}</td>
              </tr>))}</tbody></table>
        </div>
      )}
    </div>
  );
}

// ---------- Review Queue ----------
function ReviewTab({ queue, reload }) {
  const [busy, setBusy] = useState(null);
  const act = async (cid, path, body) => {
    setBusy(cid);
    try { await api.post(`/admin/campaigns/${cid}/${path}`, body || {}); toast.success("Done"); reload(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };
  const reject = async (cid) => {
    const reason = window.prompt("Rejection reason (required):");
    if (!reason) return;
    act(cid, "reject", { reason });
  };
  if (!queue || queue.length === 0) return <Empty>No campaigns awaiting review. (Self-service advertising is not activated.)</Empty>;
  return (
    <div className="space-y-2" data-testid="mc-review">
      {queue.map((c) => (
        <Card key={c.id} className="p-3 flex items-center justify-between gap-3" data-testid={`mc-review-row-${c.id}`}>
          <div className="min-w-0">
            <div className="font-medium truncate">{c.title || c.id}</div>
            <div className="text-xs text-slate-500">{c.product} · {c.placement} · {c.start_date} ({c.duration_days}d) · {c.lifecycle_state}</div>
          </div>
          <div className="flex gap-2 shrink-0">
            {c.lifecycle_state === "SUBMITTED" && <Button size="sm" variant="outline" disabled={busy === c.id} onClick={() => act(c.id, "review-start")}>Start Review</Button>}
            {c.lifecycle_state === "UNDER_REVIEW" && <Button size="sm" disabled={busy === c.id} onClick={() => act(c.id, "approve")}>Approve</Button>}
            {c.lifecycle_state === "UNDER_REVIEW" && <Button size="sm" variant="outline" disabled={busy === c.id} onClick={() => reject(c.id)}>Reject</Button>}
          </div>
        </Card>
      ))}
    </div>
  );
}

// ---------- Spotlight Inventory ----------
function InventoryTab() {
  const [placement, setPlacement] = useState("DASHBOARD_SPOTLIGHT");
  const today = new Date().toISOString().slice(0, 10);
  const to = new Date(Date.now() + 20 * 864e5).toISOString().slice(0, 10);
  const [data, setData] = useState(null);
  useEffect(() => {
    let ok = true;
    api.get(`/admin/inventory-calendar`, { params: { placement, from_date: today, to_date: to } })
      .then((r) => { if (ok) setData(r.data); }).catch(() => setData({ dates: [] }));
    return () => { ok = false; };
  }, [placement]);
  return (
    <div className="space-y-3" data-testid="mc-inventory">
      <div className="flex gap-2 flex-wrap">
        {SPOTLIGHT_PLACEMENTS.map((p) => (
          <button key={p} data-testid={`mc-inv-place-${p}`} onClick={() => setPlacement(p)}
            className={`text-xs px-3 py-1.5 rounded-full border ${placement === p ? "bg-[#0F2C59] text-white border-[#0F2C59]" : "bg-white text-slate-600 border-slate-200"}`}>{p}</button>
        ))}
      </div>
      <div className="text-xs text-slate-500">Asia/Manila advertising dates · capacity {data?.dates?.[0]?.capacity ?? 5} per date</div>
      <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-7 gap-2">
        {(data?.dates || []).map((d) => {
          const cap = d.capacity ?? 5; const occ = (d.held || 0) + (d.reserved || 0); const full = cap && occ >= cap;
          return (
            <div key={d.advertising_date} data-testid={`mc-inv-day-${d.advertising_date}`}
              className={`rounded-lg border p-2 text-center ${full ? "border-rose-300 bg-rose-50" : occ > 0 ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"}`}>
              <div className="text-[11px] text-slate-500">{d.advertising_date.slice(5)}</div>
              <div className="text-sm font-bold text-slate-800">{occ}/{cap ?? "∞"}</div>
              {full && <div className="text-[10px] font-semibold text-rose-600">FULL</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- Listing Credits ----------
function CreditsTab({ cfg }) {
  const [uid, setUid] = useState("");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const lookup = async () => {
    if (!uid.trim()) return;
    setErr(""); setData(null);
    try { const r = await api.get(`/admin/wallets/${uid.trim()}/transactions`, { params: { page: 1, page_size: 25 } }); setData(r.data); }
    catch (e) { setErr(apiError(e)); }
  };
  return (
    <div className="space-y-3" data-testid="mc-credits">
      <Pill className="bg-slate-200 text-slate-700">Listing Credit Enforcement: OFF</Pill>
      <div className="flex gap-2">
        <Input data-testid="mc-credits-uid" placeholder="Enter user/professional ID" value={uid} onChange={(e) => setUid(e.target.value)} className="max-w-sm" />
        <Button data-testid="mc-credits-lookup" onClick={lookup}>Look up wallet</Button>
      </div>
      {err && <div className="text-sm text-rose-600">{err}</div>}
      {data && (
        <>
          <Card className="p-4 flex flex-wrap gap-6" data-testid="mc-wallet-summary">
            <div><div className="text-[11px] uppercase text-slate-400">Balance</div><div className="text-xl font-bold">{data.wallet.balance} credits</div></div>
            <div><div className="text-[11px] uppercase text-slate-400">Status</div><div className="text-sm font-semibold">{data.wallet.status}</div></div>
            <div><div className="text-[11px] uppercase text-slate-400">In-flight ops</div><div className="text-sm font-semibold">{data.wallet.in_flight_ops}</div></div>
          </Card>
          {data.items.length === 0 ? <Empty>No Listing Credit activity yet for this wallet.</Empty> : (
            <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="text-left text-[11px] uppercase text-slate-400 border-b border-slate-200">
              <th className="px-3 py-2">Type</th><th className="px-3 py-2">Amount</th><th className="px-3 py-2">Balance after</th><th className="px-3 py-2">Reason</th><th className="px-3 py-2">Date</th></tr></thead>
              <tbody>{data.items.map((t) => (
                <tr key={t.id} className="border-b border-slate-100"><td className="px-3 py-2">{t.transaction_type}</td>
                  <td className={`px-3 py-2 font-semibold ${t.amount < 0 ? "text-rose-600" : "text-emerald-600"}`}>{t.amount > 0 ? "+" : ""}{t.amount}</td>
                  <td className="px-3 py-2">{t.balance_after ?? "—"}</td><td className="px-3 py-2 max-w-[240px] truncate">{t.reason || "—"}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{(t.created_at || "").slice(0, 10)}</td></tr>))}</tbody></table></div>
          )}
        </>
      )}
    </div>
  );
}

// ---------- Payments ----------
function PaymentsTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/admin/payments`, { params: { page: 1, page_size: 25 } }).then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  }, []);
  return (
    <div className="space-y-3" data-testid="mc-payments">
      <Pill className="bg-slate-200 text-slate-700">Customer Payments: NOT ACTIVATED</Pill>
      {(!data || data.items.length === 0) ? <Empty>No payment transactions. Customer payments are not activated (MANUAL provider is admin/testing only).</Empty> : (
        <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="text-left text-[11px] uppercase text-slate-400 border-b border-slate-200">
          <th className="px-3 py-2">Payment</th><th className="px-3 py-2">Provider</th><th className="px-3 py-2">Amount</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Created</th></tr></thead>
          <tbody>{data.items.map((p) => (
            <tr key={p.id} className="border-b border-slate-100" data-testid={`mc-pay-row-${p.id}`}>
              <td className="px-3 py-2 max-w-[160px] truncate">{p.id}</td><td className="px-3 py-2">{p.provider}</td>
              <td className="px-3 py-2">{peso ? peso((p.amount_centavos || 0) / 100) : `₱${((p.amount_centavos || 0) / 100).toFixed(2)}`}</td>
              <td className="px-3 py-2"><Pill className="bg-slate-100 text-slate-700">{p.status}</Pill></td>
              <td className="px-3 py-2 text-xs text-slate-500">{(p.created_at || "").slice(0, 10)}</td></tr>))}</tbody></table></div>
      )}
    </div>
  );
}

// ---------- Pricing & Placements ----------
function PricingTab({ cfg }) {
  const packages = cfg?.packages || []; const placements = cfg?.placement_registry || [];
  return (
    <div className="space-y-5" data-testid="mc-pricing">
      <div>
        <div className="text-sm font-semibold text-slate-800 mb-2">Pricing packages ({packages.length})</div>
        <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="text-left text-[11px] uppercase text-slate-400 border-b border-slate-200">
          <th className="px-3 py-2">Code</th><th className="px-3 py-2">Product</th><th className="px-3 py-2">Duration</th><th className="px-3 py-2">Price</th><th className="px-3 py-2">Placement</th><th className="px-3 py-2">State</th></tr></thead>
          <tbody>{packages.map((p) => (
            <tr key={p.package_code} className="border-b border-slate-100" data-testid={`mc-pkg-${p.package_code}`}>
              <td className="px-3 py-2 font-mono text-xs">{p.package_code}</td><td className="px-3 py-2">{p.product}</td>
              <td className="px-3 py-2">{p.duration_days ? `${p.duration_days}d` : "—"}</td>
              <td className="px-3 py-2">₱{(((p.price_centavos ?? p.price_php * 100) || 0) / 100).toLocaleString()}</td>
              <td className="px-3 py-2 text-xs">{p.placement || "—"}</td>
              <td className="px-3 py-2"><Pill className={p.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}>{p.active ? "ACTIVE" : "INACTIVE"}</Pill></td></tr>))}</tbody></table></div>
      </div>
      <div>
        <div className="text-sm font-semibold text-slate-800 mb-2">Placement registry ({placements.length})</div>
        <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="text-left text-[11px] uppercase text-slate-400 border-b border-slate-200">
          <th className="px-3 py-2">Placement</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Self-serve product</th></tr></thead>
          <tbody>{placements.map((p) => (
            <tr key={p.key} className="border-b border-slate-100" data-testid={`mc-place-${p.key}`}>
              <td className="px-3 py-2 font-mono text-xs">{p.key}</td>
              <td className="px-3 py-2"><Pill className="bg-slate-100 text-slate-700">{p.status}</Pill></td>
              <td className="px-3 py-2 text-xs">{p.self_serve_product || "—"}</td></tr>))}</tbody></table></div>
      </div>
    </div>
  );
}

export default function MonetizationConsole() {
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return TAB_KEYS.includes(t) ? t : "overview";
  });
  const [cfg, setCfg] = useState(null);
  const [legacy, setLegacy] = useState([]);
  const [managed, setManaged] = useState([]);
  const [queue, setQueue] = useState([]);
  const load = async () => {
    try {
      const [c, l, m, q] = await Promise.all([
        api.get("/admin/advertising/config"),
        api.get("/admin/ad-campaigns"),
        api.get("/admin/campaigns"),
        api.get("/admin/campaigns/review-queue"),
      ]);
      setCfg(c.data); setLegacy(l.data || []); setManaged(m.data || []); setQueue(q.data || []);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="monetization-console">
      <div className="flex items-center gap-2 mb-4">
        <Megaphone className="h-5 w-5 text-[#C9A227]" />
        <h2 className="font-display text-xl font-bold text-slate-900">Advertising &amp; Monetization</h2>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto gap-1" data-testid="mc-tabs">
          {[["overview", "Overview"], ["sponsored", "Sponsored Ads"], ["featured", "Featured Professionals"], ["campaigns", "Campaigns"], ["review", "Review Queue"], ["inventory", "Spotlight Inventory"], ["credits", "Listing Credits"], ["payments", "Payments"], ["pricing", "Pricing & Placements"]].map(([k, l]) => (
            <TabsTrigger key={k} value={k} data-testid={`mc-tab-${k}`}>{l}</TabsTrigger>
          ))}
        </TabsList>
        <div className="mt-4">
          <TabsContent value="overview"><OverviewTab cfg={cfg} legacy={legacy} managed={managed} /></TabsContent>
          <TabsContent value="sponsored"><SponsoredAdsAdmin /></TabsContent>
          <TabsContent value="featured"><FeaturedProfessionalsAdmin /></TabsContent>
          <TabsContent value="campaigns"><CampaignsTab legacy={legacy} /></TabsContent>
          <TabsContent value="review"><ReviewTab queue={queue} reload={load} /></TabsContent>
          <TabsContent value="inventory"><InventoryTab /></TabsContent>
          <TabsContent value="credits"><CreditsTab cfg={cfg} /></TabsContent>
          <TabsContent value="payments"><PaymentsTab /></TabsContent>
          <TabsContent value="pricing"><PricingTab cfg={cfg} /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
